/** `MM:SS` or `HH:MM:SS`, the two spellings the API reads an event clock in. */
const CLOCK_SHAPE = /^(?:(\d{1,2}):)?(\d{1,3}):(\d{2})$/
const HIGHEST_SECOND = 59

const twoDigits = (value: number): string =>
  value < 10 ? `0${value}` : `${value}`

/**
 * A period clock as a number of seconds, or `null` when the text is not a
 * clock. It is read the way the API reads one: `MM:SS` or `HH:MM:SS`, seconds
 * no higher than 59, minutes past the hour carried into hours (`65:00` is an
 * hour and five minutes).
 */
export const readClockSeconds = (clock?: string | null): number | null => {
  if (typeof clock !== 'string') {
    return null
  }
  const parts = CLOCK_SHAPE.exec(clock.trim())
  if (!parts || Number(parts[3]) > HIGHEST_SECOND) {
    return null
  }
  return Number(parts[1] || 0) * 3600 + Number(parts[2]) * 60 + Number(parts[3])
}

/**
 * A clock in the form the API stores it, `HH:MM:SS`, or `null` when the text
 * is not a clock: `5:00` is `00:05:00` and `65:00` is `01:05:00`.
 *
 * This is the form to send. Sent as typed, the API refuses a single-digit
 * minute (`5:00`), and a clock past the hour in minutes (`00:65:00`) is stored
 * as `00:00:00`.
 */
export const toClockTime = (clock?: string | null): string | null => {
  const seconds = readClockSeconds(clock)
  if (seconds === null) {
    return null
  }
  return [
    twoDigits(Math.floor(seconds / 3600)),
    twoDigits(Math.floor((seconds % 3600) / 60)),
    twoDigits(seconds % 60),
  ].join(':')
}

/** The minutes and seconds of a clock, every minute counted in the minutes. */
export interface ClockMinutesAndSeconds {
  minutes: number
  seconds: number
}

/**
 * A clock as the minutes and seconds a clock field shows, the hours folded
 * into the minutes (`01:05:00` is 65 and 0, `01:50:00` is 110 and 0), or
 * `null` when the text is not a clock.
 */
export const readClockMinutesAndSeconds = (
  clock?: string | null
): ClockMinutesAndSeconds | null => {
  const seconds = readClockSeconds(clock)
  if (seconds === null) {
    return null
  }
  return { minutes: Math.floor(seconds / 60), seconds: seconds % 60 }
}

/** A whole number of zero or more, as typed into a number field, or `null`. */
const readWholeNumber = (value: number | string): number | null => {
  if (typeof value === 'string' && value.trim() === '') {
    return null
  }
  const number = Number(value)
  return Number.isInteger(number) && number >= 0 ? number : null
}

/**
 * The minutes and seconds typed into a clock field as the clock to send, in
 * the form the API stores it (`65` and `7` is `01:05:07`), or `null` while
 * they are not a clock: a box left empty, seconds past 59, minutes past the
 * three digits the API reads.
 */
export const clockFromMinutesAndSeconds = (
  minutes: number | string,
  seconds: number | string
): string | null => {
  const wholeMinutes = readWholeNumber(minutes)
  const wholeSeconds = readWholeNumber(seconds)
  if (wholeMinutes === null || wholeSeconds === null) {
    return null
  }
  return toClockTime(`${wholeMinutes}:${twoDigits(wholeSeconds)}`)
}

/**
 * A clock in the form the scoring screen keeps the last one it knows for the
 * period in play (`game.currentPeriodTime`): minutes and seconds, every minute
 * counted in the minutes (`00:05:07` is `5:07`, `01:05:00` is `65:00`). `null`
 * when the text is not a clock.
 */
export const toLastKnownClock = (clock?: string | null): string | null => {
  const seconds = readClockSeconds(clock)
  if (seconds === null) {
    return null
  }
  return `${Math.floor(seconds / 60)}:${twoDigits(seconds % 60)}`
}
